import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Play } from 'lucide-react';

interface CTASectionProps {
  cmsData?: Record<string, unknown>;
}

const CTASection: React.FC<CTASectionProps> = ({ cmsData }) => {
  const navigate = useNavigate();

  return (
    <section className="py-24 lg:py-32 relative overflow-hidden">
      {/* Stripe-style gradient background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#635bff] via-[#7c3aed] to-[#00d4ff]" />
        
        {/* Animated gradient overlay */}
        <div className="absolute inset-0 opacity-50">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/10 to-transparent" />
        </div>
        
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }} />
        </div>
        
        {/* Floating orbs */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] opacity-30">
          <div className="w-full h-full bg-gradient-to-br from-white/20 to-transparent rounded-full blur-3xl animate-float" />
        </div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] opacity-20">
          <div className="w-full h-full bg-gradient-to-tr from-[#00d4ff]/50 to-transparent rounded-full blur-3xl animate-float-delayed" />
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white leading-tight">
            {(cmsData?.headline as string) || 'Ready to transform your ads?'}
          </h2>
          <p className="mt-6 text-lg lg:text-xl text-white/80 leading-relaxed">
            {(cmsData?.subheadline as string) || 'Join the publishers, agencies, and brands already using Adventive to create interactive ads that perform.'}
          </p>
          
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => navigate('/demo')}
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-[#635bff] bg-white rounded-full hover:bg-slate-100 transition-all hover:shadow-xl hover:shadow-black/20 group"
            >
              Request Demo
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => navigate('/gallery')}
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white bg-white/10 backdrop-blur-sm border border-white/20 rounded-full hover:bg-white/20 hover:border-white/30 transition-all group"
            >
              <Play className="mr-2 h-5 w-5" />
              View Ad Gallery
            </button>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-3 gap-8 pt-12 border-t border-white/20">
            <div>
              <div className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white">200,000+</div>
              <div className="mt-2 text-sm text-white/60 font-medium">Ads built</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white">9.5B+</div>
              <div className="mt-2 text-sm text-white/60 font-medium">Impressions served</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white">1,000+</div>
              <div className="mt-2 text-sm text-white/60 font-medium">Years of video playback</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
